import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Play, Info, VolumeX } from 'lucide-react';

/* Écran de fin de série façon Netflix : le film rétrécit en haut à gauche,
   une recommandation s'affiche en grand avec compte à rebours vers sa bande-annonce. */
export default function EndScreen({ rec, phase, setPhase, tvMode, onWatch, onMoreInfo, onExit, onTrailerStart, onResume, creditsEnded }) {
  const [count, setCount] = useState(5);
  const [focus, setFocus] = useState(0);
  const [trailerMuted, setTrailerMuted] = useState(false);
  const trailerRef = useRef(null);

  useEffect(() => { setTrailerMuted(false); }, [phase]);

  // Compte à rebours 5s → bande-annonce automatique
  useEffect(() => {
    if (phase !== 'offer') return;
    setCount(5);
    const iv = setInterval(() => {
      setCount(c => {
        if (c <= 1) { clearInterval(iv); setPhase(rec.trailer_url ? 'trailer' : 'poster'); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => { setFocus(0); }, [phase]);
  useEffect(() => { if (phase === 'trailer') onTrailerStart?.(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // TV : retour automatique à la navigation après 5 min sur l'affiche (la veille prendra le relais)
  useEffect(() => {
    if (phase !== 'poster' || !tvMode) return;
    const t = setTimeout(onExit, 5 * 60 * 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, tvMode]);

  const buttons = phase === 'offer'
    ? [
        { label: 'Regarder', icon: Play, action: onWatch, testid: 'endscreen-watch-btn' },
        rec.trailer_url ? { label: `Bande-annonce dans ${count}`, icon: Play, action: () => setPhase('trailer'), testid: 'endscreen-trailer-btn' } : null,
      ].filter(Boolean)
    : [
        { label: 'Regarder', icon: Play, action: onWatch, testid: 'endscreen-watch-btn' },
        { label: "Plus d'infos", icon: Info, action: onMoreInfo, testid: 'endscreen-info-btn' },
      ];

  // Navigation D-pad TV (focus -1 = flèche retour, -2 = mini-lecteur du générique)
  useEffect(() => {
    if (!tvMode) return;
    const onKey = (e) => {
      const k = e.keyCode;
      if (k === 38) { e.preventDefault(); setFocus(f => (f >= 0 ? (creditsEnded ? -1 : -2) : -1)); }
      else if (k === 40) { e.preventDefault(); setFocus(f => (f === -1 ? (creditsEnded ? 0 : -2) : (f === -2 ? 0 : f))); }
      else if (k === 37) { e.preventDefault(); setFocus(f => (f < 0 ? f : Math.max(0, f - 1))); }
      else if (k === 39) { e.preventDefault(); setFocus(f => (f < 0 ? f : Math.min(buttons.length - 1, f + 1))); }
      else if (k === 13) {
        e.preventDefault();
        if (focus === -1) onExit();
        else if (focus === -2) { if (!creditsEnded) onResume?.(); }
        else buttons[Math.min(focus, buttons.length - 1)]?.action();
      }
      else if (k === 27 || k === 8 || k === 10009 || k === 461) { e.preventDefault(); onExit(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tvMode, buttons, focus, onExit, onResume, creditsEnded]);

  // Générique entièrement terminé : la petite fenêtre disparaît, on quitte son focus
  useEffect(() => {
    if (creditsEnded) setFocus(f => (f === -2 ? 0 : f));
  }, [creditsEnded]);

  const backdrop = rec.banner_url || rec.thumbnail_url;

  return (
    <div className="absolute inset-0 z-[55] bg-black overflow-hidden animate-fadeIn" data-testid="endscreen-overlay">
      {/* Fond : affiche paysage ou bande-annonce */}
      {phase === 'trailer' && rec.trailer_url ? (
        <>
          <video
            ref={trailerRef}
            src={rec.trailer_url}
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            onEnded={() => setPhase('poster')}
            onError={() => setPhase('poster')}
            onCanPlay={(e) => {
              // Lecture automatique AVEC le son ; repli muet uniquement si le navigateur bloque
              const v = e.target;
              v.muted = false;
              v.volume = 1;
              if (v.paused) {
                v.play().catch(() => {
                  v.muted = true;
                  setTrailerMuted(true);
                  v.play().catch(() => setPhase('poster'));
                });
              }
            }}
            data-testid="endscreen-trailer-video"
          />
          {trailerMuted && (
            <button
              onClick={() => { const v = trailerRef.current; if (v) { v.muted = false; v.volume = 1; } setTrailerMuted(false); }}
              data-testid="endscreen-unmute-btn"
              className="absolute top-6 right-8 z-[64] flex items-center gap-2 px-4 py-2 rounded-full bg-black/60 border border-white/40 text-white text-sm font-semibold hover:bg-black/80 transition-colors">
              <VolumeX size={16} /> Activer le son
            </button>
          )}
        </>
      ) : (
        <img src={backdrop} alt={rec.title} className="absolute inset-0 w-full h-full object-cover" data-testid="endscreen-backdrop" />
      )}
      {phase !== 'trailer' && <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40" />}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 to-transparent" />

      {/* Cadre de focus TV sur le mini-lecteur (le générique continue dedans) */}
      {tvMode && focus === -2 && !creditsEnded && (
        <div className="absolute top-[13%] left-[4%] w-[24%] h-[24%] z-[63] pointer-events-none rounded ring-[3px] ring-white shadow-[0_0_24px_rgba(255,255,255,0.45)]" data-testid="endscreen-mini-focus">
          <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-black/75 border border-white/40 text-white text-sm font-semibold">
            Continuer le générique
          </span>
        </div>
      )}

      {/* Retour à la navigation */}
      <button onClick={onExit} data-testid="endscreen-back-btn"
        className={`absolute top-6 left-8 z-[64] flex items-center gap-3 transition-colors ${tvMode && focus === -1 ? 'text-white' : 'text-white/80 hover:text-white'}`}>
        <span className={`w-11 h-11 rounded-full bg-black/50 border flex items-center justify-center transition-all ${tvMode && focus === -1 ? 'border-white ring-2 ring-white scale-110 bg-black/80' : 'border-white/40'}`}>
          <ArrowLeft size={22} />
        </span>
        <span className="text-base font-medium drop-shadow">Retour à la navigation</span>
      </button>

      {/* Infos + actions */}
      <div className="absolute right-[6%] bottom-[10%] max-w-xl flex flex-col items-start gap-4 z-[58] text-left">
        <h2 className="text-4xl sm:text-5xl font-extrabold text-white drop-shadow-lg" data-testid="endscreen-title">{rec.title}</h2>
        {phase === 'offer' && rec.description && (
          <p className="text-white/85 text-base leading-relaxed line-clamp-3 drop-shadow">{rec.description}</p>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          {buttons.map((b, i) => (
            <button key={b.testid} data-testid={b.testid} onClick={b.action}
              className={`flex items-center gap-2 px-6 py-3 rounded font-semibold text-base transition-all cursor-pointer
                ${i === 0 ? 'bg-white text-black hover:bg-white/85' : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'}
                ${tvMode && focus === i ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-105' : ''}`}>
              <b.icon size={18} fill={b.icon === Play ? 'currentColor' : 'none'} /> {b.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
